import { Button } from '@talentmesh/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { cn } from '@talentmesh/ui/lib/utils';

import { useState } from 'react';

import { type HTMLPropsRef, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type ActionIconProps } from '../../types/mrt-ui-props';

interface Props<TData extends MRT_RowData> extends ActionIconProps, HTMLPropsRef<HTMLButtonElement> {
    table: MRT_TableInstance<TData>;
}

export const MRT_ToggleFullScreenButton = <TData extends MRT_RowData>({
    table: {
        getState,
        options: {
            icons: { IconMaximize, IconMinimize },
            localization: { toggleFullScreen },
        },
        setIsFullScreen,
    },
    title,
    ...rest
}: Props<TData>) => {
    const { isFullScreen } = getState();
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleToggleFullScreen = () => {
        setTooltipOpen(false);
        setIsFullScreen((current) => !current);
    };

    return (
        <Tooltip onOpenChange={setTooltipOpen} open={tooltipOpen}>
            <TooltipTrigger asChild>
                <Button
                    {...rest}
                    aria-label={title ?? toggleFullScreen}
                    className={cn('text-muted-foreground h-9 w-9', rest?.className)}
                    onClick={handleToggleFullScreen}
                    onMouseEnter={() => setTooltipOpen(true)}
                    onMouseLeave={() => setTooltipOpen(false)}
                    size="icon-2xs"
                    variant="ghost"
                >
                    {isFullScreen ? <IconMinimize className="size-4" /> : <IconMaximize className="size-4" />}
                </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{title ?? toggleFullScreen}</TooltipContent>
        </Tooltip>
    );
};
